import React from "react";
import type { Control, FieldPath } from "react-hook-form";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/global-components/form";
import { ComboboxView } from "@/app/global-components/combobox-view";
import ArabicDatePicker from "@/app/global-components/arbic-date-picker";
import {
  Box,
  Typography,
  FormControl as MuiFormControl,
  FormControlLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
  TextField,
  alpha,
  useTheme,
} from "@mui/material";
import { ProfileSchema } from "@/schema";

interface ProfileFieldViewProps {
  name: FieldPath<z.infer<typeof ProfileSchema>>;
  label: string;
  placeholder: string;
  description?: string;
  disabled?: boolean;
  inputType?: "text" | "radio" | "date" | "combobox";
  options?: { value: string; label: string | React.ReactNode }[];
  formControl: Control<z.infer<typeof ProfileSchema>>;
}

const ProfileFieldView: React.FC<ProfileFieldViewProps> = ({
  name,
  disabled,
  label,
  placeholder,
  inputType,
  options,
  formControl,
}) => {
  const theme = useTheme();

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field }) => (
        <FormItem className="mb-4">
          {label && (
            <FormLabel className="mb-2 text-xs lg:text-base font-semibold text-gray-700">
              {label}
            </FormLabel>
          )}
          <FormControl>
            {inputType === "radio" && options ? (
              <MuiFormControl component="fieldset">
                <MuiRadioGroup
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  sx={{
                    "& .MuiFormControlLabel-root": {
                      margin: "4px 0",
                      padding: "8px 12px",
                      borderRadius: 1,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04
                        ),
                      },
                      "&.Mui-checked": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08
                        ),
                      },
                    },
                    "& .MuiRadio-root": {
                      color: theme.palette.grey[400],
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  {options.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio disabled={disabled} />}
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.label}
                        </Typography>
                      }
                    />
                  ))}
                </MuiRadioGroup>
              </MuiFormControl>
            ) : inputType === "date" ? (
              <Box sx={{ position: "relative" }}>
                <ArabicDatePicker
                  disabled={disabled}
                  date={
                    field.value && typeof field.value === "string"
                      ? new Date(field.value)
                      : null
                  }
                  setDate={(date) => {
                    if (date) {
                      const localDateString = date.toLocaleDateString("en-CA");
                      field.onChange(localDateString);
                    } else {
                      field.onChange(null);
                    }
                  }}
                />
              </Box>
            ) : inputType === "combobox" && options ? (
              <ComboboxView
                disabled={disabled}
                placeholder={placeholder}
                options={options}
                value={field.value ? field.value : ""}
                onChange={(value) => field.onChange(value)}
              />
            ) : (
              <TextField
                {...field}
                disabled={disabled}
                type={inputType || "text"}
                placeholder={placeholder}
                value={typeof field.value === "string" ? field.value : ""}
                variant="outlined"
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value);
                }}
                sx={{
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            )}
          </FormControl>
          <FormMessage className="text-red-500 text-xs sm:text-sm mt-1" />
        </FormItem>
      )}
    />
  );
};

export default ProfileFieldView;
